<!--
  Right-side flyout for the Pricing Engine, launched from the "Price" button in the Pipeline
  grid (ProcessingV2.vue). Unlike the /polly-pricer route (still used by the sidebar nav's
  Scenario Mode link and opened as its own page), this stays an overlay on top of the grid so
  a processor doesn't lose their scroll position or filters when they just want a quick price.
  Takes up 90% of the viewport width so there's real room to work in Polly's UI.
  It duplicates PollyPricerView's launch-url fetch rather than reusing that view directly,
  since PollyPricerView's PageHeaderPortal would otherwise teleport its header into the app's
  top bar and stomp on the Pipeline page's own title while this is open.
-->
<template>
  <transition name="polly-flyout-fade">
    <div v-if="open" class="polly-flyout-backdrop" @click.self="close"></div>
  </transition>

  <transition name="polly-flyout-slide">
    <aside v-if="open" class="polly-flyout" role="complementary">
      <header class="polly-flyout__header">
        <div class="polly-flyout__title">
          <i class="fa-light fa-calculator"></i>
          Pricing Engine
        </div>
        <button type="button" class="polly-flyout__close" @click="close">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </header>

      <div class="polly-flyout__body">
        <div v-if="loading" class="polly-flyout__state">Loading the Pricing Engine...</div>

        <div v-else-if="error" class="polly-flyout__blocked">
          <div class="polly-flyout__blocked-icon"><i class="fa-duotone fa-circle-exclamation"></i></div>
          <div>
            <div class="polly-flyout__blocked-title">Couldn't open the Pricing Engine</div>
            <div class="polly-flyout__blocked-text">{{ error }}</div>
          </div>
        </div>

        <iframe
          v-else-if="launchUrl"
          :src="launchUrl"
          class="polly-flyout__frame"
          title="Pricing Engine"
          allow="clipboard-write"
        ></iframe>
      </div>
    </aside>
  </transition>
</template>

<script>
import apiService from '../../api/api.service.js';
import auth from '../../authConfig.js';

function pick(obj, ...keys) {
  if (!obj || typeof obj !== 'object') return undefined;
  const normalize = k => String(k).toLowerCase().replace(/[_-]/g, '');
  const wanted = keys.map(normalize);
  for (const actualKey of Object.keys(obj)) {
    if (wanted.includes(normalize(actualKey)) && obj[actualKey] !== undefined && obj[actualKey] !== null) {
      return obj[actualKey];
    }
  }
  return undefined;
}

export default {
  name: 'PollyPricerFlyout',
  props: {
    open: { type: Boolean, default: false },
    loanId: { type: String, default: null },
    orgTicker: { type: String, default: null }
  },
  emits: ['close'],
  data() {
    return {
      loading: true,
      error: '',
      launchUrl: ''
    };
  },
  watch: {
    open(val) {
      if (val) {
        window.addEventListener('keydown', this.onKeyDown);
        this.load();
      } else {
        window.removeEventListener('keydown', this.onKeyDown);
        // Drop the loaded frame once closed so re-opening on a different loan doesn't
        // flash the previous loan's pricer before the new launch url comes back.
        this.launchUrl = '';
        this.error = '';
      }
    }
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeyDown);
  },
  methods: {
    close() {
      this.$emit('close');
    },
    onKeyDown(e) {
      if (e.key === 'Escape') this.close();
    },
    async load() {
      this.loading = true;
      this.error = '';

      try {
        const tokenResponse = await auth.acquireTokenResponse();
        const azureId = localStorage.getItem('azure_id') || null;

        if (!azureId) {
          throw new Error('Could not determine your signed-in identity. Try signing out and back in.');
        }

        const request = {
          azureId,
          loanId: this.loanId || null,
          audienceId: null,
          quoteScenarioId: null
        };

        const response = await apiService.GetPollyPortalLaunchUrl(request, tokenResponse.accessToken);

        const wrapper = response?.data || {};
        const result = pick(wrapper, 'data', 'Data') || {};

        const isSuccess = pick(wrapper, 'isSuccess', 'issuccess', 'IsSuccess');
        const message = pick(wrapper, 'message', 'Message') || '';
        const launchUrl = pick(result, 'launchUrl', 'LaunchUrl');

        if (!isSuccess || !launchUrl) {
          throw new Error(message || 'The Pricing Engine did not return a launch URL.');
        }

        this.launchUrl = launchUrl;
      } catch (err) {
        this.error =
          err?.response?.data?.message ||
          err?.response?.data?.Message ||
          err?.message ||
          'Unable to open the Pricing Engine.';
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.polly-flyout-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  z-index: 1200;
}

.polly-flyout {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 90vw;
  background: #fff;
  z-index: 1201;
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 32px rgba(15, 23, 42, 0.18);
}

.polly-flyout__header {
  height: 64px;
  flex: 0 0 auto;
  padding: 0 14px 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  background: linear-gradient(#ffffff, #fbfcff);
}

.polly-flyout__title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 900;
  font-size: 18px;
  color: #0f172a;
  letter-spacing: -0.2px;
}

.polly-flyout__close {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 10px rgba(15, 23, 42, 0.08);
  color: rgba(15, 23, 42, 0.72);
}

.polly-flyout__body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.polly-flyout__frame {
  flex: 1 1 auto;
  width: 100%;
  border: none;
}

.polly-flyout__state {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7b8397;
  font-size: 14px;
}

.polly-flyout__blocked {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 24px;
  text-align: left;
}
.polly-flyout__blocked-icon { font-size: 32px; color: #c0392b; flex: 0 0 auto; }
.polly-flyout__blocked-title { font-size: 16px; font-weight: 700; color: #1f2a44; margin-bottom: 4px; }
.polly-flyout__blocked-text { font-size: 13px; color: #5b6478; }

.polly-flyout-slide-enter-active,
.polly-flyout-slide-leave-active {
  transition: transform 0.22s cubic-bezier(.22,1,.36,1);
}
.polly-flyout-slide-enter-from,
.polly-flyout-slide-leave-to {
  transform: translateX(100%);
}

.polly-flyout-fade-enter-active,
.polly-flyout-fade-leave-active {
  transition: opacity 0.18s ease;
}
.polly-flyout-fade-enter-from,
.polly-flyout-fade-leave-to {
  opacity: 0;
}
</style>
